import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import Navigation from "../../components/Navigation";
import Seo, { breadcrumbs } from "../../components/Seo";
import { SITE_NAME, absUrl, toDescription, toISODate } from "../../lib/seo";
import { MetaLabel, CornerTick } from "../../brand/Brand";
import {
  subscribePublishedCaseStudies,
  subscribePublishedCaseStudy,
} from "../../caseStudies/publicApi";
import {
  Marquee,
  MaskImage,
  Parallax,
  ProgressBar,
  Reveal,
  RiseLines,
  RiseWords,
  StatCounter,
  isDark,
} from "./anim";
import "./CaseStudy.css";

const NARRATIVE = [
  { key: "challenge", n: "01", label: "The challenge" },
  { key: "approach", n: "02", label: "The approach" },
  { key: "outcome", n: "03", label: "The outcome" },
];

const LINK_LABELS = {
  web: "Visit live site",
  case: "Full write-up",
  mobile: "Mobile site",
  ios: "App Store",
  android: "Play Store",
  github: "GitHub",
  figma: "Figma",
  behance: "Behance",
  dribbble: "Dribbble",
  video: "Watch video",
  other: "Open link",
};

const CaseStudy = () => {
  const { id } = useParams();
  const reduced = useReducedMotion();

  const [cs, setCs] = useState(undefined); // undefined = loading, null = not found
  const [all, setAll] = useState([]);

  useEffect(() => subscribePublishedCaseStudy(id, setCs), [id]);
  useEffect(() => subscribePublishedCaseStudies(setAll), []);
  // Braces matter: newer Chrome returns a Promise from window.scrollTo,
  // and a concise arrow would hand that Promise to React as a "cleanup fn"
  // → "TypeError: destroy is not a function" on unmount.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const next = useMemo(() => {
    if (all.length < 2) return null;
    const i = all.findIndex((x) => x.id === id);
    return all[(i + 1) % all.length] || null;
  }, [all, id]);

  if (cs === undefined) {
    return (
      <div className="cs-page">
        <Navigation />
        <div className="cs-loading">
          <MetaLabel>Loading case…</MetaLabel>
        </div>
      </div>
    );
  }

  if (cs === null) {
    return (
      <div className="cs-page">
        {/* Unpublished or deleted case. Netlify still serves a 200 here, so
            noindex is what keeps the dead URL out of the index. */}
        <Seo
          title="Case not found — bboy creative"
          description="This case study isn't available. Browse all published work instead."
          path={`/work/${id}`}
          noindex
        />
        <Navigation />
        <div className="cs-loading">
          <MetaLabel>404 / Case not found</MetaLabel>
          <Link to="/work" className="cs-back-link">← All work</Link>
        </div>
      </div>
    );
  }

  const dark = isDark(cs.color);
  const fg = dark ? "var(--fw-cream)" : "var(--fw-ink)";
  const fgMuted = dark ? "rgba(243,239,232,0.82)" : "rgba(17,17,16,0.68)";
  const links = (cs.links || []).filter((l) => l && l.url);
  const gallery = cs.gallery || [];
  const meta = [
    ["Client", cs.client],
    ["Industry", cs.industry],
    ["Year", cs.year],
    ["Location", cs.location],
    ["Timeline", cs.durationWeeks ? `${cs.durationWeeks} weeks` : null],
  ].filter(([, v]) => v);

  return (
    <div className="cs-page" style={{ "--cs-c": cs.color || "#1f1f1f", "--cs-fg": fg }}>
      <Seo
        title={`${cs.title} — Work — bboy creative`}
        description={
          cs.description ||
          `${cs.title} — a case study from bboy creative${
            cs.client ? ` for ${cs.client}` : ""
          }.`
        }
        path={`/work/${cs.id}`}
        image={cs.heroImageUrl}
        type="article"
        modifiedTime={toISODate(cs.updatedAt)}
        jsonLd={[
          {
            "@type": "CreativeWork",
            "@id": absUrl(`/work/${cs.id}#case`),
            name: cs.title,
            headline: cs.title,
            description: toDescription(cs.description),
            url: absUrl(`/work/${cs.id}`),
            ...(cs.heroImageUrl ? { image: cs.heroImageUrl } : {}),
            ...(cs.year ? { dateCreated: String(cs.year) } : {}),
            ...(cs.industry ? { about: cs.industry } : {}),
            ...(cs.tag ? { genre: cs.tag } : {}),
            creator: { "@type": "Organization", name: SITE_NAME },
            // The client is the party the work was produced for — schema.org
            // models that as sourceOrganization, not author.
            ...(cs.client
              ? {
                  sourceOrganization: {
                    "@type": "Organization",
                    name: cs.client,
                  },
                }
              : {}),
          },
          breadcrumbs([
            { name: "Home", path: "/" },
            { name: "Work", path: "/work" },
            { name: cs.title, path: `/work/${cs.id}` },
          ]),
        ]}
      />

      <ProgressBar />
      <Navigation />

      {/* ── Hero: color field ─────────────────────────────────────── */}
      <section className="cs-hero" style={{ background: "var(--cs-c)", color: fg }}>
        <div className="cs-hero-inner cs-frame">
          <CornerTick pos="tl" color={fgMuted} />
          <CornerTick pos="tr" color={fgMuted} />
          <CornerTick pos="bl" color={fgMuted} />
          <CornerTick pos="br" color={fgMuted} />

          <div className="cs-hero-top">
            <Link to="/work" className="cs-back" style={{ color: fg }}>
              ← All work
            </Link>
            <MetaLabel color={fgMuted}>{cs.tag}</MetaLabel>
          </div>

          <div className="cs-hero-center">
            {cs.client && (
              <Reveal delay={0.05}>
                <MetaLabel color={fgMuted}>{cs.client}</MetaLabel>
              </Reveal>
            )}
            <RiseLines as="h1" className="cs-h1" lines={[cs.title]} delay={0.12} />
            {cs.description && (
              <p className="cs-tagline">
                <RiseWords text={cs.description} delay={0.4} />
              </p>
            )}
          </div>

          <div className="cs-hero-bottom">
            <div className="cs-hero-meta">
              {meta.slice(1).map(([k, v]) => (
                <span key={k} className="cs-hero-meta-item">
                  <MetaLabel color={fgMuted}>{k}</MetaLabel>
                  <span className="cs-hero-meta-value">{v}</span>
                </span>
              ))}
            </div>
            <motion.span
              className="cs-scroll-cue"
              style={{ color: fgMuted }}
              animate={reduced ? undefined : { y: [0, 8, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              aria-hidden="true"
            >
              scroll ↓
            </motion.span>
          </div>
        </div>

        {/* Hero image bleeds out of the color field */}
        {cs.heroImageUrl && (
          <div className="cs-hero-img-wrap">
            <Parallax range={reduced ? 0 : 50}>
              <MaskImage
                src={cs.heroImageUrl}
                alt={`${cs.title} — hero`}
                className="cs-hero-img cs-frame"
                delay={0.15}
              />
            </Parallax>
          </div>
        )}
      </section>

      {/* spacer only when the hero image overlaps */}
      {cs.heroImageUrl && <div className="cs-hero-overlap-spacer" />}

      {/* ── Body: sticky rail + narrative ─────────────────────────── */}
      <section className="cs-body cs-container">
        <aside className="cs-rail">
          <div className="cs-rail-sticky">
            {meta.map(([k, v]) => (
              <div className="cs-rail-item" key={k}>
                <MetaLabel>{k}</MetaLabel>
                <div className="cs-rail-value">{v}</div>
              </div>
            ))}

            {cs.deliverables?.length > 0 && (
              <div className="cs-rail-item">
                <MetaLabel>Deliverables</MetaLabel>
                <ul className="cs-rail-list">
                  {cs.deliverables.map((d, i) => (
                    <li key={i}>
                      <span className="cs-diamond">◆</span> {d}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {links.length > 0 && (
              <div className="cs-rail-item cs-rail-links">
                {links.map((l, i) => (
                  <a
                    key={i}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`cs-link-btn ${i === 0 ? "is-primary" : ""}`}
                  >
                    {l.label || LINK_LABELS[l.type] || "Open link"} ↗
                  </a>
                ))}
              </div>
            )}
          </div>
        </aside>

        <div className="cs-narrative">
          {NARRATIVE.filter(({ key }) => cs[key]).map(({ key, n, label }, i) => (
            <article className="cs-section" key={key}>
              <Reveal delay={0.05}>
                <div className="cs-section-head">
                  <span className="cs-section-num">{n}</span>
                  <h2 className="cs-section-title">
                    <RiseWords text={label} />
                  </h2>
                </div>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="cs-section-body">{cs[key]}</p>
              </Reveal>
              {/* Weave gallery shots between sections when available */}
              {gallery[i] && (
                <Parallax range={reduced ? 0 : 32}>
                  <MaskImage
                    src={gallery[i]}
                    alt={`${cs.title} — detail ${i + 1}`}
                    ratio={i === 0 ? "16 / 10" : "4 / 3"}
                    className="cs-section-img"
                  />
                </Parallax>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* ── Results band ──────────────────────────────────────────── */}
      {cs.results?.length > 0 && (
        <section className="cs-results">
          <div className="cs-container">
            <Reveal>
              <MetaLabel color="var(--fw-muted-2)">§ Results</MetaLabel>
            </Reveal>
            <div className="cs-results-grid">
              {cs.results.map((r, i) => (
                <Reveal key={i} delay={i * 0.1}>
                  <div className="cs-result">
                    <StatCounter value={r} className="cs-result-value" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Tech marquee ──────────────────────────────────────────── */}
      {cs.techStack?.length > 0 && (
        <section className="cs-stack" aria-label="Tech stack">
          <Marquee speed={22}>
            {cs.techStack.map((t, i) => (
              <span className="cs-stack-item" key={i}>
                <span className="cs-diamond">◆</span>
                <span>{t}</span>
              </span>
            ))}
          </Marquee>
        </section>
      )}

      {/* ── Remaining gallery ─────────────────────────────────────── */}
      {gallery.length > NARRATIVE.filter(({ key }) => cs[key]).length && (
        <section className="cs-gallery cs-container">
          <Reveal>
            <MetaLabel>§ Gallery</MetaLabel>
          </Reveal>
          <div className="cs-gallery-grid">
            {gallery
              .slice(NARRATIVE.filter(({ key }) => cs[key]).length)
              .map((src, i) => (
                <MaskImage
                  key={i}
                  src={src}
                  alt={`${cs.title} — gallery ${i + 1}`}
                  ratio="4 / 5"
                  className="cs-gallery-img"
                  delay={(i % 2) * 0.12}
                />
              ))}
          </div>
        </section>
      )}

      {/* ── Testimonial ───────────────────────────────────────────── */}
      {cs.testimonial?.quote && (
        <section className="cs-quote cs-container">
          <span className="cs-quote-mark" aria-hidden="true">“</span>
          <blockquote className="cs-quote-text">
            <RiseWords text={cs.testimonial.quote} stagger={0.03} />
          </blockquote>
          <Reveal delay={0.2}>
            <div className="cs-quote-author">
              <span className="cs-rule" />
              {cs.testimonial.author}
              {cs.testimonial.role ? `, ${cs.testimonial.role}` : ""}
            </div>
          </Reveal>
        </section>
      )}

      {/* ── Next case ─────────────────────────────────────────────── */}
      {next && (
        <Link
          to={`/work/${next.id}`}
          className="cs-next"
          style={{
            "--next-c": next.color || "#1f1f1f",
            color: isDark(next.color) ? "var(--fw-cream)" : "var(--fw-ink)",
          }}
        >
          <div className="cs-container cs-next-inner">
            <div className="cs-next-top">
              <MetaLabel color="currentColor">Next case</MetaLabel>
              <MetaLabel color="currentColor">{next.tag}</MetaLabel>
            </div>
            <div className="cs-next-title">
              {next.title}
              <span className="cs-next-arrow" aria-hidden="true"> →</span>
            </div>
            {next.description && (
              <div className="cs-next-desc">{next.description}</div>
            )}
          </div>
        </Link>
      )}
    </div>
  );
};

export default CaseStudy;
